import { useCallback, ChangeEvent } from 'react';
import { UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { PolicyForm } from '../policy-dialog';

import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';

interface PolicyPrimaryOrganizationSectionProps {
  watch: UseFormWatch<PolicyForm>;
  setValue: UseFormSetValue<PolicyForm>;
  disabled: boolean;
  fieldName: `actionData.${number}.userModelTemplate.organizations`;
}

export default function PolicyPrimaryOrganizationSection({
  watch,
  setValue,
  disabled,
  fieldName,
}: PolicyPrimaryOrganizationSectionProps) {
  const organizations = watch(fieldName);
  const primaryOrganization = organizations?.find((org) => org.primary);

  const handlePrimaryOrgChanges = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (primaryOrganization) {
        setValue(
          fieldName,
          organizations?.map((org) => {
            if (org.primary) {
              switch (e.target.name) {
                case 'title':
                  return { ...org, title: e.target.value };
                case 'description':
                  return { ...org, description: e.target.value };
                case 'department':
                  return { ...org, department: e.target.value };
                case 'name':
                  return { ...org, name: e.target.value };
                case 'costCenter':
                  return { ...org, costCenter: e.target.value };
                case 'location':
                  return { ...org, location: e.target.value };
                case 'symbol':
                  return { ...org, symbol: e.target.value };
                default:
                  return org;
              }
            } else {
              return org;
            }
          }),
          { shouldDirty: true }
        );
      } else {
        const newOrg = {
          title: '',
          description: '',
          department: '',
          name: '',
          costCenter: '',
          location: '',
          symbol: '',
          domain: '',
          primary: true,
          type: 'unknown',
        };

        switch (e.target.name) {
          case 'title':
            newOrg.title = e.target.value;
            break;
          case 'description':
            newOrg.description = e.target.value;
            break;
          case 'department':
            newOrg.department = e.target.value;
            break;
          case 'name':
            newOrg.name = e.target.value;
            break;
          case 'costCenter':
            newOrg.costCenter = e.target.value;
            break;
          case 'location':
            newOrg.location = e.target.value;
            break;
          case 'symbol':
            newOrg.symbol = e.target.value;
            break;
        }

        setValue(fieldName, [...(organizations || []), newOrg], {
          shouldDirty: true,
        });
      }
    },
    [organizations, primaryOrganization, setValue, fieldName]
  );

  return (
    <>
      <Tooltip
        arrow
        title={
          disabled
            ? `You need the "Edit Organizations" permission to edit this field.`
            : ''
        }
      >
        <span>
          <TextField
            value={primaryOrganization?.title || ''}
            name="title"
            onChange={(e) => handlePrimaryOrgChanges(e)}
            autoComplete="off"
            label="Job Title"
            sx={{ minWidth: 400 }}
            disabled={disabled}
          />
        </span>
      </Tooltip>
      <Tooltip
        arrow
        title={
          disabled
            ? `You need the "Edit Organizations" permission to edit this field.`
            : ''
        }
      >
        <span>
          <TextField
            value={primaryOrganization?.description || ''}
            onChange={(e) => handlePrimaryOrgChanges(e)}
            name="description"
            autoComplete="off"
            label="Type of Employee"
            sx={{ minWidth: 400 }}
            disabled={disabled}
          />
        </span>
      </Tooltip>
      <Tooltip
        arrow
        title={
          disabled
            ? `You need the "Edit Organizations" permission to edit this field.`
            : ''
        }
      >
        <span>
          <TextField
            value={primaryOrganization?.department || ''}
            onChange={(e) => handlePrimaryOrgChanges(e)}
            name="department"
            autoComplete="off"
            label="Department"
            sx={{ minWidth: 400 }}
            disabled={disabled}
          />
        </span>
      </Tooltip>
      <Tooltip
        arrow
        title={
          disabled
            ? `You need the "Edit Organizations" permission to edit this field.`
            : ''
        }
      >
        <span>
          <TextField
            value={primaryOrganization?.name || ''}
            onChange={(e) => handlePrimaryOrgChanges(e)}
            name="name"
            autoComplete="off"
            label="Organization Name"
            sx={{ minWidth: 400 }}
            disabled={disabled}
          />
        </span>
      </Tooltip>
      <Tooltip
        arrow
        title={
          disabled
            ? `You need the "Edit Organizations" permission to edit this field.`
            : ''
        }
      >
        <span>
          <TextField
            value={primaryOrganization?.costCenter || ''}
            onChange={(e) => handlePrimaryOrgChanges(e)}
            name="costCenter"
            autoComplete="off"
            label="Cost Center"
            sx={{ minWidth: 400 }}
            disabled={disabled}
          />
        </span>
      </Tooltip>
      <Tooltip
        arrow
        title={
          disabled
            ? `You need the "Edit Organizations" permission to edit this field.`
            : ''
        }
      >
        <span>
          <TextField
            value={primaryOrganization?.location || ''}
            onChange={(e) => handlePrimaryOrgChanges(e)}
            name="location"
            autoComplete="off"
            label="Location"
            sx={{ minWidth: 400 }}
            disabled={disabled}
          />
        </span>
      </Tooltip>
      <Tooltip
        arrow
        title={
          disabled
            ? `You need the "Edit Organizations" permission to edit this field.`
            : ''
        }
      >
        <span>
          <TextField
            value={primaryOrganization?.symbol || ''}
            onChange={(e) => handlePrimaryOrgChanges(e)}
            name="symbol"
            autoComplete="off"
            label="Stock Symbol"
            sx={{ minWidth: 400 }}
            disabled={disabled}
          />
        </span>
      </Tooltip>
    </>
  );
}
